import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useUserDetails } from '../store/slices/authSlice';
import { setActiveHelpDetails, whatsappAddDetails } from '../store/slices/tenantSlices/helpDeskSlice';
import { whatsappRedirectMessageGet } from '../store/slices/tenantSlices/configurationSlice';
import { useBrandingDetails } from '../store/slices/adminSlices/brandingDetailSlice';
import { convertToMask } from '../utils/common';
import defaultBrandLogo from '../assets/images/logos/Collex-main.svg';

// Every WhatsApp URL is served with a Cross-Origin-Opener-Policy that moves its tab into another
// browsing context group, which discards the tab name - so a WhatsApp tab can never be found again
// and each click left one behind. The launcher is same-origin, keeps its name, and is reused.
const WHATSAPP_TAB_NAME = 'callex_whatsapp';

// How the launcher receives its payload, so nothing lands in the URL. Logging out clears it with
// the rest of localStorage. Keep in step with public/whatsapp-launch.html.
const WHATSAPP_LAUNCH_KEY = 'callex:whatsapp-launch';

// Shared by the WhatsApp icon on Call History, Live Call History and the CRM screen.
const useWhatsappHandoff = () => {
  const user = useUserDetails();
  const brandingDetails = useBrandingDetails();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const openHelpDesk = async (primaryNumber) => {
    try {
      const payload = { from: `91${primaryNumber}@c.us`, name: primaryNumber };
      const response = await dispatch(whatsappAddDetails(payload)).unwrap();
      if (response && response?.message) {
        dispatch(
          setActiveHelpDetails({
            ...response?.data,
            id: response?.data?._id,
            name: response?.data?.from_name,
            activeTab: response?.data?.assignedToId ? 'mine' : 'unassigned'
          })
        );
        navigate('/help-desk');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const openWhatsappRedirect = async (primaryNumber) => {
    const digits = String(primaryNumber).replace(/\D/g, '');
    const phone = digits.length === 10 ? `91${digits}` : digits;
    const isNumberMaskingEnabled = user?.process_details?.numberMasking === 'true';

    // A process may have no message configured yet, and a failed lookup is not worth blocking the
    // hand-off - both just open the chat with an empty draft.
    let message = '';
    try {
      const process_id = Number(user?.process_details?.process_id ?? user?.process_id);
      const response = await dispatch(
        whatsappRedirectMessageGet({ process_id, page: 1, limit: 10 })
      ).unwrap();
      message = response?.data?.[0]?.message || '';
    } catch (error) {
      console.log('error', error);
    }

    // A static file cannot read the store or import the bundled logo, so branding is handed over
    // with the same fallbacks Auth.jsx and App.js use. The default favicon is left out because the
    // bundler inlines it and the launcher already ships that image.
    try {
      window.localStorage.setItem(
        WHATSAPP_LAUNCH_KEY,
        JSON.stringify({
          phone,
          // Masking hides the number on screen, not from WhatsApp: the launcher shows this and
          // still opens the chat on `phone`. Masked from the raw digits so it reads the same as
          // the report the agent clicked from.
          display: isNumberMaskingEnabled ? convertToMask(digits) : '',
          text: message,
          logo: brandingDetails?.headerlogo_path || defaultBrandLogo,
          icon: brandingDetails?.favicon_path || '',
          brand: brandingDetails?.header_text || process.env.REACT_APP_SITE_TITLE || '',
          footer: brandingDetails?.footer_text || process.env.REACT_APP_FOOTER_TEXT || ''
        })
      );
    } catch (error) {
      console.log('error', error);
    }

    // The nonce forces the reused tab to reload; an unchanged URL would only refocus it. Nothing
    // here navigates this page, so the SIP session and MainLayout's beforeunload guard are safe.
    const nonce = Math.random().toString(36).slice(2, 8);
    const launcher = window.open(
      `${process.env.PUBLIC_URL}/whatsapp-launch.html?s=${nonce}`,
      WHATSAPP_TAB_NAME
    );
    if (launcher) launcher.focus();
  };

  const handleWhatsappClick = (primaryNumber) => {
    if (user?.is_redirect_to_whatsapp) {
      openWhatsappRedirect(primaryNumber);
      return;
    }
    openHelpDesk(primaryNumber);
  };

  return handleWhatsappClick;
};

export default useWhatsappHandoff;
